import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import { database } from '../../Firebase';
import { ref, onValue } from 'firebase/database';

const AccountSelectionModal = ({ isOpen, onClose, onSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Fetch Accounts from Firebase
  useEffect(() => {
    const accountsRef = ref(database, 'Grampanchayat/Panipuravtha/AccountDetails');
    const unsubscribe = onValue(accountsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const loadedAccounts = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        }));
        // Sort by Account No if possible, or name
        loadedAccounts.sort((a, b) => (parseInt(a.accountNo) || 0) - (parseInt(b.accountNo) || 0));
        setAccounts(loadedAccounts);
      } else {
        setAccounts([]);
      }
    });

    return () => unsubscribe();
  }, []);

  if (!isOpen) return null;

  // Filter Logic
  const filteredAccounts = accounts.filter(acc => 
    (acc.accountName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) || 
    (acc.accountNo?.toString() || '').includes(searchTerm)
  );

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredAccounts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredAccounts.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl overflow-hidden border border-[#267c77]">
        
        {/* Header */}
        <div className="bg-[#267c77] text-white p-3 flex justify-between items-center">
          <h2 className="text-lg font-medium">
            खातावणी माहिती
          </h2>
          <button onClick={onClose} className="text-white hover:text-gray-300">
            <FiX size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 bg-white">
            
            {/* Search */}
            <div className="mb-4">
                <label className="block text-gray-700 mb-1 font-medium">शोधणे</label>
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1); // Reset to page 1 on search
                    }}
                    placeholder="खातावणी क्र./ खातावणीचे नाव भरा."
                    className="w-full p-2 border border-[#267c77]/30 rounded focus:ring-2 focus:ring-[#267c77] focus:outline-none"
                />
            </div>

            {/* Table */}
            <div className="border border-[#267c77] mb-4">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-[#267c77] text-white">
                        <tr>
                            <th className="p-2 border-r border-[#1e6b66] w-24 text-center">खातावणी नं.</th>
                            <th className="p-2 border-r border-[#1e6b66]">खातावणीचे नाव</th>
                            <th className="p-2">खाते गट</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems.length > 0 ? (
                            currentItems.map((acc, idx) => (
                                <tr 
                                    key={acc.id} 
                                    onClick={() => {
                                        onSelect(acc);
                                        onClose();
                                    }}
                                    className="cursor-pointer hover:bg-gray-50 border-b border-gray-300 text-sm"
                                >
                                    <td className="p-2 border-r border-gray-300 text-center font-medium">{acc.accountNo}</td>
                                    <td className="p-2 border-r border-gray-300 font-medium">{acc.accountName}</td>
                                    <td className="p-2">{acc.accountGroupName}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="3" className="p-8 text-center text-gray-500">
                                    No records found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Footer */}
            <div className="flex justify-center items-center gap-1 bg-[#267c77] p-1 text-white text-sm rounded-sm">
                 <button 
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    className="px-2 py-1 bg-white/20 hover:bg-white/30 disabled:opacity-50 rounded"
                 >
                    |&lt;
                 </button>
                 <button 
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-2 py-1 bg-white/20 hover:bg-white/30 disabled:opacity-50 rounded"
                 >
                    &lt;&lt;
                 </button>
                 
                 <span className="bg-[#1e6b66] px-3 py-1 rounded font-bold border border-[#1e6b66]">
                    {currentPage}
                 </span>
                 
                 <button 
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-2 py-1 bg-white/20 hover:bg-white/30 disabled:opacity-50 rounded"
                 >
                    &gt;&gt;
                 </button>
                 <button 
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    className="px-2 py-1 bg-white/20 hover:bg-white/30 disabled:opacity-50 rounded"
                 >
                    &gt;|
                 </button>

                 <select 
                    value={itemsPerPage}
                    onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                    }}
                    className="ml-2 bg-white text-black p-0.5 rounded outline-none cursor-pointer h-7"
                 >
                     <option value={5}>5</option>
                     <option value={10}>10</option>
                     <option value={20}>20</option>
                 </select>
            </div>

        </div>
      </div>
    </div>
  );
};

export default AccountSelectionModal;
